import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { GymModuleKey, Role, ViewAsRole } from '@bulk/types';
import type { CurrentUser } from '@bulk/validation';
import { api, configureApi } from './api';

const TOKEN_KEY = 'bulk.token';
const GYM_KEY = 'bulk.gymId';
const GYM_ROLE_KEY = 'bulk.gymRole';
const VIEW_AS_KEY = 'bulk.viewAs';
const VIEW_AS_MEMBER_KEY = 'bulk.viewAsMember';

/** A staff role to work the admin as, or one of a practice gym's made-up members whose member area to view. */
export type ViewAsChoice = ViewAsRole | { memberId: string };

export interface AuthMode {
  mode: 'dev' | 'supabase';
  supabaseUrl: string | null;
  supabaseAnonKey: string | null;
}

interface AuthState {
  user: CurrentUser | null;
  loading: boolean;
  mode: AuthMode | null;
  gymId: string | null;
  gymRole: Role | null;
  /** A platform admin's own staff role at the gym (`ownRole`) when they chose it over Bulk's gym admin standing. */
  setGymId: (gymId: string | null, role?: Role | null) => void;
  /** The role a gym admin, or Bulk, is working the admin as, or MEMBER while viewing a made-up member's area; a gym switch or signing out returns them to their own. */
  viewAs: ViewAsRole | 'MEMBER' | null;
  setViewAs: (choice: ViewAsChoice | null) => void;
  login: (email: string, password: string) => Promise<void>;
  register: (input: { email: string; password: string; firstName: string; lastName: string; phone?: string; inviteToken?: string }) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  registerAtThisGym: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

let supabase: SupabaseClient | null = null;
export async function getSupabase(mode: AuthMode): Promise<SupabaseClient> {
  if (supabase) return supabase;
  const { createClient } = await import('@supabase/supabase-js');
  supabase = createClient(mode.supabaseUrl!, mode.supabaseAnonKey!);
  return supabase;
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // storage unavailable
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(() => readStorage(TOKEN_KEY));
  const [gymId, setGymIdState] = useState<string | null>(() => readStorage(GYM_KEY));
  const [gymRole, setGymRoleState] = useState<Role | null>(() => readStorage(GYM_ROLE_KEY) as Role | null);
  const [viewAs, setViewAsState] = useState<ViewAsRole | 'MEMBER' | null>(() => readStorage(VIEW_AS_KEY) as ViewAsRole | 'MEMBER' | null);
  const [viewAsMemberId, setViewAsMemberId] = useState<string | null>(() => readStorage(VIEW_AS_MEMBER_KEY));
  const [mode, setMode] = useState<AuthMode | null>(null);

  useEffect(() => {
    configureApi({
      getToken: async () => {
        if (mode?.mode === 'supabase') {
          const client = await getSupabase(mode);
          const { data } = await client.auth.getSession();
          return data.session?.access_token ?? null;
        }
        return token;
      },
      getGymId: () => gymId,
      getGymRole: () => gymRole,
      getViewAs: () => (viewAs ? { role: viewAs, memberId: viewAs === 'MEMBER' ? viewAsMemberId : null } : null),
    });
  }, [token, gymId, gymRole, viewAs, viewAsMemberId, mode]);

  useEffect(() => {
    api<AuthMode>('/api/v1/auth/mode')
      .then(setMode)
      .catch(() => setMode({ mode: 'dev', supabaseUrl: null, supabaseAnonKey: null }));
  }, []);

  useEffect(() => {
    if (mode?.mode !== 'supabase') return;
    let unsub: (() => void) | undefined;
    getSupabase(mode).then((client) => {
      const { data } = client.auth.onAuthStateChange((_event, session) => {
        setToken(session?.access_token ?? null);
        queryClient.invalidateQueries({ queryKey: ['me'] });
      });
      unsub = () => data.subscription.unsubscribe();
    });
    return () => unsub?.();
  }, [mode, queryClient]);

  const meQuery = useQuery({
    queryKey: ['me', token, gymId, gymRole, viewAs, viewAsMemberId, mode?.mode],
    enabled: mode !== null && (mode.mode === 'supabase' || token !== null),
    retry: false,
    queryFn: async () => {
      try {
        return await api<CurrentUser>('/api/v1/auth/me');
      } catch (err) {
        if ((err as { status?: number }).status === 401) {
          if (mode?.mode === 'dev') {
            writeStorage(TOKEN_KEY, null);
            setToken(null);
          }
          return null;
        }
        throw err;
      }
    },
  });

  const setViewAs = useCallback((choice: ViewAsChoice | null) => {
    const role = choice === null || typeof choice === 'string' ? choice : 'MEMBER';
    const memberId = choice !== null && typeof choice === 'object' ? choice.memberId : null;
    writeStorage(VIEW_AS_KEY, role);
    writeStorage(VIEW_AS_MEMBER_KEY, memberId);
    setViewAsState(role);
    setViewAsMemberId(memberId);
  }, []);

  useEffect(() => {
    if (viewAs && meQuery.data && meQuery.data.viewAs === null) setViewAs(null);
  }, [viewAs, meQuery.data, setViewAs]);

  const setGymId = useCallback(
    (next: string | null, role: Role | null = null) => {
      writeStorage(GYM_KEY, next);
      writeStorage(GYM_ROLE_KEY, role);
      setGymIdState(next);
      setGymRoleState(role);
      setViewAs(null);
    },
    [setViewAs],
  );

  useEffect(() => {
    if (gymRole && meQuery.data && meQuery.data.currentGym?.role !== gymRole) {
      writeStorage(GYM_ROLE_KEY, null);
      setGymRoleState(null);
    }
  }, [gymRole, meQuery.data]);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['me'] });
  }, [queryClient]);

  const login = useCallback(
    async (email: string, password: string) => {
      if (mode?.mode === 'supabase') {
        const client = await getSupabase(mode);
        const { error } = await client.auth.signInWithPassword({ email, password });
        if (error) throw new Error(error.message);
      } else {
        const session = await api<{ accessToken: string }>('/api/v1/auth/dev/login', { body: { email, password } });
        writeStorage(TOKEN_KEY, session.accessToken);
        setToken(session.accessToken);
      }
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    [mode, queryClient],
  );

  const register = useCallback(
    async (input: { email: string; password: string; firstName: string; lastName: string; phone?: string; inviteToken?: string }) => {
      if (mode?.mode === 'supabase') {
        const client = await getSupabase(mode);
        const { error } = await client.auth.signUp({ email: input.email, password: input.password, options: { data: { first_name: input.firstName, last_name: input.lastName } } });
        if (error) throw new Error(error.message);
        await api('/api/v1/auth/register-member', input.inviteToken ? { body: { inviteToken: input.inviteToken } } : { method: 'POST' });
      } else {
        const session = await api<{ accessToken: string }>('/api/v1/auth/dev/register', { body: input });
        writeStorage(TOKEN_KEY, session.accessToken);
        setToken(session.accessToken);
      }
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    [mode, queryClient],
  );

  const registerAtThisGym = useCallback(async () => {
    await api('/api/v1/auth/register-member', { method: 'POST' });
    await queryClient.invalidateQueries({ queryKey: ['me'] });
  }, [queryClient]);

  const logout = useCallback(async () => {
    if (mode?.mode === 'supabase') {
      const client = await getSupabase(mode);
      await client.auth.signOut();
    }
    writeStorage(TOKEN_KEY, null);
    setToken(null);
    setViewAs(null);
    queryClient.clear();
  }, [mode, queryClient, setViewAs]);

  const value = useMemo<AuthState>(
    () => ({
      user: meQuery.data ?? null,
      loading: mode === null || (meQuery.isLoading && meQuery.fetchStatus !== 'idle'),
      mode,
      gymId,
      gymRole,
      setGymId,
      viewAs,
      setViewAs,
      login,
      register,
      logout,
      refresh,
      registerAtThisGym,
    }),
    [meQuery.data, meQuery.isLoading, meQuery.fetchStatus, mode, gymId, gymRole, setGymId, viewAs, setViewAs, login, register, logout, refresh, registerAtThisGym],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

export function useHasPermission() {
  const { user } = useAuth();
  return (permission: string) => user?.permissions.includes(permission as never) ?? false;
}

/** Whether Bulk has this module on for the gym the person works in; a module that is off is not drawn anywhere. */
export function useModuleOn() {
  const { user } = useAuth();
  return (module: GymModuleKey) => !user?.disabledModules.includes(module);
}
